import { useCallback, useMemo } from 'react';

import { type Hcphco } from '@/core/graphql/generated/types';
import { paths } from '@/core/router/paths';
import { HcoProfile } from '@/pages/HcoProfilePage/_hooks/useHcoData';
import { hcoUrl } from '@/utils/hco/hcoUrl';
import { getHcpDisplayName } from '@/utils/hcp/hcpDisplayName';
import { hcpUrl } from '@/utils/hcp/hcpUrl';
import { returnValidIntl as isValidIntl } from '@/utils/isValidIntl';
import { replaceRouteParams } from '@/utils/url/replaceRouteParams';
import { validateNotUnknown } from '@/utils/validateNotUnknown';

const getUniqueDegrees = (profile: Hcphco) => {
	if (profile.__typename === 'HCP') {
		const { allDegreeCda_V: allDegrees, degree1Cda_V: primaryDegree } = profile;

		const fullDegreesList = allDegrees
			? allDegrees
					.split(',')
					.map(d => d.trim())
					.filter(Boolean)
			: [];

		return Array.from(new Set([primaryDegree, ...fullDegreesList])).filter(
			Boolean
		) as string[];
	}

	return [];
};

type AffiliationLike = {
	vid_V: string;
	isPrimaryRelationship_V?: boolean | null;
};

const getPrimaryOrFirstAffiliation = <T extends AffiliationLike>(
	affiliations: T[] | null | undefined
): T | undefined => {
	if (!affiliations || affiliations.length === 0) return undefined;

	return (
		affiliations.find(aff => aff.isPrimaryRelationship_V) ?? affiliations[0]
	);
};

type NameBuilderOptions = {
	withInternationalName?: boolean;
	withPrefix?: boolean;
	withShortName?: boolean;
	withNickname?: boolean;
	inline?: boolean;
};

type ProfileNameResult = {
	name: string;
	degrees: string;
};

const getIntlName = (profile: Hcphco | null) => {
	if (!profile) return undefined;

	if (profile.__typename === 'HCP') {
		const intlName = [
			profile.firstNameIntl_V,
			profile.middleNameIntl_V,
			profile.lastNameIntl_V
		]
			.filter(Boolean)
			.join(' ')
			.trim();

		return isValidIntl(profile.firstNameCda_V, intlName);
	}
	if (profile.__typename === 'HCO') {
		return isValidIntl(profile.hcoNameCda_V, profile.hcoNameIntl_V);
	}
	return;
};

const buildProfileName = (
	profile: Hcphco | null,
	options: NameBuilderOptions = {}
): ProfileNameResult => {
	if (!profile) return { name: '', degrees: '' };

	const {
		withInternationalName = false,
		withPrefix = false,
		withShortName = false,
		withNickname = false
	} = options;

	if (profile.__typename === 'HCP') {
		const baseName = getHcpDisplayName({
			firstName: profile.firstNameCda_V,
			middleName: profile.middleNameCda_V,
			lastName: profile.lastNameCda_V,
			suffix: profile.suffixCda_V,
			prefix: withPrefix ? profile.prefixCda_V : undefined,
			nickname: withNickname ? profile.nickname_V : undefined
		});

		let finalName = baseName;
		let degrees = '';

		const uniqueDegrees = getUniqueDegrees(profile);
		if (uniqueDegrees.length > 0) {
			degrees = `, ${uniqueDegrees.join(', ')}`;
		}

		if (withInternationalName) {
			const intlName = getIntlName(profile);
			if (intlName && intlName !== baseName) {
				finalName = `${finalName} (${intlName})`;
			}
		}

		return { name: finalName, degrees };
	}

	if (profile.__typename === 'HCO') {
		let finalName = profile.hcoNameCda_V;

		if (withInternationalName) {
			const intlName = getIntlName(profile);
			if (intlName && intlName !== finalName) {
				finalName = `${finalName} (${intlName})`;
			}
		}

		if (withShortName) {
			if (profile.hcoNameShort_V) {
				finalName = `${finalName} (${profile.hcoNameShort_V})`;
			}
		}

		return { name: finalName ?? '', degrees: '' };
	}

	return { name: '', degrees: '' };
};

export const useProfileMetaData = (profile: Hcphco | null) => {
	const primaryAddress = useMemo(() => {
		if (!profile) return undefined;

		const addresses = profile.addresses;

		const pa = addresses?.filter(a => a.primaryCda_V);
		if (pa && pa.length > 0) {
			return pa[0];
		}
		return addresses?.length > 0 ? addresses[0] : undefined;
	}, [profile]);

	const internationalName = useMemo(() => getIntlName(profile), [profile]);

	const englishName = useMemo(() => {
		if (profile?.__typename === 'HCO') {
			return isValidIntl(profile.hcoNameCda_V, profile.hcoNameEnglish_V);
		}
		return;
	}, [profile]);

	const getProfileName = useCallback(
		(options?: NameBuilderOptions) => buildProfileName(profile, options),
		[profile]
	);

	const primarySpecialty = useMemo(() => {
		if (!profile) return undefined;

		if (profile.__typename === 'HCP') {
			return profile?.hcpSpecialties?.find(spec => spec.primary);
		}

		return undefined;
	}, [profile]);

	const specialtyAndType = useMemo(() => {
		if (!profile) return undefined;

		if (profile.__typename === 'HCP') {
			const specName = primarySpecialty?.specialty?.name;

			return [validateNotUnknown(specName), profile?.hcpType?.name]
				.filter(Boolean)
				.join(' • ');
		}

		if (profile.__typename === 'HCO') {
			return profile.hcoType?.name ?? '';
		}

		return '';
	}, [profile, primarySpecialty]);

	const primaryAffiliation = useMemo(() => {
		if (!profile) return null;

		if (profile.__typename === 'HCP') {
			const aff = getPrimaryOrFirstAffiliation(profile.affiliations?.results);

			if (aff?.parentHco.__typename === 'HCO') {
				return aff.parentHco;
			}
		} else if (profile.__typename === 'HCO') {
			const aff = getPrimaryOrFirstAffiliation(
				(profile as HcoProfile).parentAffiliations?.results
			);

			if (aff?.parentHco.__typename === 'HCO') {
				return aff.parentHco;
			}
		}

		return null;
	}, [profile]);

	const profileLink = useMemo(() => {
		if (profile?.__typename === 'HCP') {
			const name = {
				firstName: profile?.firstNameCda_V,
				lastName: profile?.lastNameCda_V,
				id: profile?.vid_V || ''
			};

			if (Object.values(name).some(v => !v)) return '';

			return replaceRouteParams(paths.hcp, {
				hcpId: hcpUrl(name)
			});
		} else if (profile?.__typename === 'HCO') {
			const name = {
				name: profile?.hcoNameCda_V,
				id: profile?.vid_V || ''
			};

			if (Object.values(name).some(v => !v)) return '';

			return replaceRouteParams(paths.hco, {
				hcoId: hcoUrl(name)
			});
		}

		return '#';
	}, [profile]);

	return {
		internationalName,
		englishName,
		getProfileName,
		primarySpecialty,
		specialtyAndType,
		primaryAffiliation,
		primaryAddress,
		profileLink
	};
};
